// 10.04.26: сюда подключён диалог прогресса async approve-all.
// Связи: page передаёт job/state, а OperationProgressDialog показывает realtime/polling progress.
import { channelDisplayNames, getQueueSourceLabelForQueueItem, statusLabels } from '@/types/broadcastQueue';
import { DeliveryPreviewPanel } from '@/components/features/broadcast/DeliveryPreview';
import { Button, ConfirmDialog, OperationProgressDialog } from '@/components/ui';
import type {
  BroadcastMode,
  BulkChangeChannelReasonSummary,
  BulkChangeChannelResult,
  BroadcastOperationJob,
} from '@/types/broadcastQueue';

type BroadcastQueueDialogsProps = {
  confirmApproveAll: boolean;
  onCloseApproveAll: () => void;
  onConfirmApproveAll: () => void;
  confirmClear: boolean;
  onCloseClear: () => void;
  onConfirmClear: () => void;
  confirmToggleMode: boolean;
  onCloseToggleMode: () => void;
  onConfirmToggleMode: () => void;
  showRejectDialog: boolean;
  onCloseRejectDialog: () => void;
  onConfirmReject: () => void;
  pendingCount: number;
  selectedPendingCount: number;
  actionLoading: boolean;
  mode: BroadcastMode;
  bulkChannelResult: {
    message: string;
    skippedMessage?: string;
    failureSummary?: string;
    failedItems?: BulkChangeChannelResult[];
    reasonSummary?: BulkChangeChannelReasonSummary[];
  } | null;
  onCloseBulkChannelResult: () => void;
  previewItemId: string | null;
  tenantId?: string;
  canChangeChannel: boolean;
  onQueueChanged: () => void;
  onClosePreview: () => void;
  operationJob: BroadcastOperationJob | null;
  isOperationJobWsConnected: boolean;
  onCloseOperationJob: () => void;
};

// 24.03.26: диалоги и preview-panel вынесли из page-layout.
export function BroadcastQueueDialogs({
  confirmApproveAll,
  onCloseApproveAll,
  onConfirmApproveAll,
  confirmClear,
  onCloseClear,
  onConfirmClear,
  confirmToggleMode,
  onCloseToggleMode,
  onConfirmToggleMode,
  showRejectDialog,
  onCloseRejectDialog,
  onConfirmReject,
  pendingCount,
  selectedPendingCount,
  actionLoading,
  mode,
  bulkChannelResult,
  onCloseBulkChannelResult,
  previewItemId,
  tenantId,
  canChangeChannel,
  onQueueChanged,
  onClosePreview,
  operationJob,
  isOperationJobWsConnected,
  onCloseOperationJob,
}: BroadcastQueueDialogsProps) {
  return (
    <>
      <ConfirmDialog
        isOpen={confirmApproveAll}
        onClose={onCloseApproveAll}
        onConfirm={onConfirmApproveAll}
        title="Одобрить все рассылки"
        message={`Вы уверены, что хотите одобрить и отправить ${pendingCount} сообщений? Все ожидающие рассылки будут отправлены пациентам.`}
        confirmText={`Одобрить ${pendingCount} сообщений`}
        variant="primary"
        isLoading={actionLoading}
      />

      <ConfirmDialog
        isOpen={confirmClear}
        onClose={onCloseClear}
        onConfirm={onConfirmClear}
        title="Очистить очередь"
        message={`Вы уверены, что хотите отклонить ВСЕ ${pendingCount} ожидающих рассылок? Сообщения НЕ будут отправлены.`}
        confirmText="Отклонить все"
        variant="danger"
        isLoading={actionLoading}
      />

      <ConfirmDialog
        isOpen={confirmToggleMode}
        onClose={onCloseToggleMode}
        onConfirm={onConfirmToggleMode}
        title={mode === 'manual' ? 'Включить автоматический режим' : 'Включить ручной режим'}
        message={
          mode === 'manual'
            ? 'ВНИМАНИЕ! В автоматическом режиме рассылки отправляются СРАЗУ без одобрения. Все новые рассылки будут отправляться автоматически. Вы уверены?'
            : 'Переключить в ручной режим? Все рассылки будут ждать одобрения перед отправкой.'
        }
        confirmText={mode === 'manual' ? 'Включить автоматический' : 'Включить ручной'}
        variant={mode === 'manual' ? 'danger' : 'primary'}
        isLoading={actionLoading}
      />

      <ConfirmDialog
        isOpen={showRejectDialog}
        onClose={onCloseRejectDialog}
        onConfirm={onConfirmReject}
        title="Отклонить рассылки"
        message={`Отклонить ${selectedPendingCount} выбранных рассылок? Сообщения НЕ будут отправлены.`}
        confirmText={`Отклонить (${selectedPendingCount})`}
        variant="danger"
        isLoading={actionLoading}
      />

      {bulkChannelResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Результат смены канала</h3>
            <p className="text-gray-600 mb-2">{bulkChannelResult.message}</p>
            {bulkChannelResult.skippedMessage && (
              <p className="text-amber-600 text-sm mb-4">{bulkChannelResult.skippedMessage}</p>
            )}
            {bulkChannelResult.reasonSummary && bulkChannelResult.reasonSummary.length > 0 && (
              <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm font-medium text-amber-900 mb-2">Сводка причин</p>
                <ul className="space-y-1 text-sm text-amber-800">
                  {bulkChannelResult.reasonSummary.map((summary) => (
                    <li key={`${summary.error_code}-${summary.reason}`}>
                      {summary.reason} ({summary.count})
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {bulkChannelResult.failedItems && bulkChannelResult.failedItems.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-900 mb-2">Проблемные элементы</p>
                <div className="max-h-72 overflow-y-auto rounded-md border border-gray-200">
                  <div className="divide-y divide-gray-100">
                    {bulkChannelResult.failedItems.map((item) => (
                      <div key={item.item_id} className="p-3 text-sm">
                        <div className="font-medium text-gray-900">{item.patient_name || item.phone || item.item_id}</div>
                        <div className="text-gray-500">
                          {(() => {
                            const eventLabel = item.event_type ? getQueueSourceLabelForQueueItem(item) || item.event_type : '—';
                            const statusLabel =
                              item.status && item.status in statusLabels
                                ? statusLabels[item.status as keyof typeof statusLabels]
                                : item.status || '—';
                            const channelLabel = item.old_channel ? channelDisplayNames[item.old_channel] || item.old_channel : '—';
                            return `${eventLabel} · ${statusLabel} · ${channelLabel}`;
                          })()}
                        </div>
                        {item.error && <div className="mt-1 text-rose-700">{item.error}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div className="flex justify-end">
              <Button onClick={onCloseBulkChannelResult}>OK</Button>
            </div>
          </div>
        </div>
      )}

      {previewItemId && (
        <DeliveryPreviewPanel
          itemId={previewItemId}
          tenantId={tenantId}
          canChangeChannel={canChangeChannel}
          onQueueChanged={onQueueChanged}
          onClose={onClosePreview}
        />
      )}

      <OperationProgressDialog
        isOpen={!!operationJob}
        title="Прогресс массового одобрения"
        job={operationJob}
        isWebSocketConnected={isOperationJobWsConnected}
        onClose={onCloseOperationJob}
      />
    </>
  );
}
